import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Container, 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper, 
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  InputAdornment
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { useUser } from '../context/UserContext';

const steps = ['Welcome', 'Set Monthly Limit'];

const OnboardingPage = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [displayName, setDisplayName] = useState('');
  const [monthlyLimit, setMonthlyLimit] = useState('');
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { currentUser } = useAuth();
  const { updateProfile, error: userError, isOnboarded } = useUser();
  const navigate = useNavigate();
  
  // Initialize display name from Firebase user
  useEffect(() => {
    if (currentUser && currentUser.displayName) {
      setDisplayName(currentUser.displayName);
    }
  }, [currentUser]);
  
  // Redirect if already onboarded
  useEffect(() => {
    if (isOnboarded) {
      navigate('/');
    }
  }, [isOnboarded, navigate]);
  
  const handleNext = () => {
    if (activeStep === 0) {
      // Validate first step
      if (!displayName.trim()) {
        setFormError('Please enter your name');
        return;
      }
      
      setFormError('');
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    } else {
      // This is the final step - submit the form
      handleSubmit();
    }
  };
  
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    setFormError('');
  };
  
  const handleSubmit = async () => {
    setFormError('');
    
    // Validate monthly limit
    if (!monthlyLimit.trim()) {
      setFormError('Please enter your monthly spending limit');
      return;
    }
    
    const limitValue = parseFloat(monthlyLimit);
    if (isNaN(limitValue) || limitValue <= 0) {
      setFormError('Please enter a valid amount greater than 0');
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      // Update user profile with display name and monthly limit
      await updateProfile({
        displayName,
        monthlyLimit: limitValue
      });
      
      // Navigation to home will happen in the useEffect above when isOnboarded changes
    } catch (error) {
      setFormError(error.message);
      setIsSubmitting(false);
    }
  };
  
  // Render step content
  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Welcome to spenvo, your gamified spend tracker!
            </Typography>
            <Typography variant="body1" paragraph>
              Let's set up your account so you can challenge yourself.
            </Typography>
            <TextField
              margin="normal"
              required
              fullWidth
              id="displayName"
              label="Your Name"
              name="displayName"
              autoFocus
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              disabled={isSubmitting}
            />
          </Box>
        );
      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Set Your Monthly Limit
              </Typography>
            <Typography variant="body2" >
            This is the maximum you want to spend on variable costs such as food, drinks, clothing, coffee, other personal spending, etc.
            <br></br>
            </Typography>
            <br></br>
          
            <Typography variant="body2" gutterBottom>
            <b>Example:</b> $5,000 (Paycheck) - $2,500 (Fixed Costs) - $500 (Savings Goal) = $2,000 (Monthly Spending Limit)
            </Typography>
            <br></br>
            <TextField
              margin="normal"
              required
              fullWidth
              id="monthlyLimit"
              label="Monthly Spending Limit"
              name="monthlyLimit"
              type="number"
              autoFocus
              value={monthlyLimit}
              onChange={(e) => setMonthlyLimit(e.target.value)}
              disabled={isSubmitting}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
            />
          </Box>
        );
      default:
        return 'Unknown step';
    }
  };
  
  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          py: 4,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: '100%',
            borderRadius: 2,
          }}
        >
          <Box sx={{ mb: 3, textAlign: 'center' }}>
          <img 
            src="/logo-nb.png" 
            alt="Spenvo Logo Blue"
            align="center"
            textalign="center" 
            style={{ height: 16, marginRight: 8 }}
            />
          </Box>
          
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          
          {(formError || userError) && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {formError || userError}
            </Alert>
          )}
          
          <Box sx={{ mt: 2, mb: 4 }}>
            {getStepContent(activeStep)}
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
              disabled={activeStep === 0 || isSubmitting}
              onClick={handleBack}
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <CircularProgress size={24} />
              ) : activeStep === steps.length - 1 ? (
                'Finish'
              ) : (
                'Next'
              )}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default OnboardingPage;